import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend);

const EngagementViewChart = ({
  responseData = [],
  micrositeName = "",
  term = "yearly",
  year,
  monthName,
  quarter,
}) => {

  // Normalize microsite name for case-insensitive matching
  const normalizedMicrosite = useMemo(() => {
    if (!micrositeName) return "";
    return micrositeName.toString().toLowerCase().replace(/[_%20]+/g, " ").trim();
  }, [micrositeName]);

  // Filter response data (even if no microsite, return all)
  const filteredData = useMemo(() => {
    if (!Array.isArray(responseData)) return [];
    return responseData.filter((item) => {
      const normalizedPath = (item.path || "")
        .toString()
        .toLowerCase()
        .replace(/[_%20]+/g, " ")
        .trim();
      return normalizedMicrosite === "" ? true : normalizedPath.includes(normalizedMicrosite);
    });
  }, [responseData, normalizedMicrosite]);

  // Month names with year
  const monthNames = useMemo(() => {
    const y = year ?? new Date().getFullYear();
    return [
      `January ${y}`,
      `February ${y}`,
      `March ${y}`,
      `April ${y}`,
      `May ${y}`,
      `June ${y}`,
      `July ${y}`,
      `August ${y}`,
      `September ${y}`,
      `October ${y}`,
      `November ${y}`,
      `December ${y}`,
    ];
  }, [year]);

  const toMonthIndex = (m) => {
    if (!m) return null;
    const numeric = Number(m);
    if (numeric >= 1 && numeric <= 12) return numeric - 1;

    const parsed = new Date(`${m} 1, ${year ?? new Date().getFullYear()}`);
    if (!isNaN(parsed)) return parsed.getMonth();
    return null;
  };

  // Build chart labels
  const labels = useMemo(() => {
    if (term === "yearly") return monthNames;

    if (term === "quarterly") {
      const q = Number(quarter) || 1;
      const quarters = {
        1: [0, 1, 2],
        2: [3, 4, 5],
        3: [6, 7, 8],
        4: [9, 10, 11],
      };
      return quarters[q].map((i) => monthNames[i]);
    }

    // Monthly
    const idx = toMonthIndex(monthName);
    return [monthNames[idx ?? new Date().getMonth()]];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term, monthNames, quarter, monthName, year]);

  // Convert label to backend key → "202510"
  const labelToKey = (label) => {
    if (!label) return null;
    const [monthLabel, yearLabel] = label.split(" ");
    const monthIndex = new Date(`${monthLabel} 1, ${yearLabel}`).getMonth() + 1;
    return `${yearLabel}${String(monthIndex).padStart(2, "0")}`;
  };

  // Build data points
  const dataPoints = useMemo(() => {
    if (!Array.isArray(filteredData)) return [];

    if (term === "yearly" || term === "quarterly") {
      return labels.map((label) => {
        const key = labelToKey(label); // ← "202510"
        if (!key) return 0;

        const match = filteredData.find((d) => `${d.year}${d.month.padStart(2, "0")}` === key);

        return match ? Number(match.visitors || 0) : 0;
      });
    }

    // Monthly (sum if multiple rows)
    const monthKey = labelToKey(labels[0]);
    const sum = filteredData
      .filter((d) => `${d.year}${d.month.padStart(2, "0")}` === monthKey)
      .reduce((acc, d) => acc + Number(d.visitors || 0), 0);

      console.log('sum',sum)

    return [sum];
  }, [filteredData, labels, term]);
  console.log('dataPoints',dataPoints)

  // Y-axis scaling
  const safe = dataPoints.length ? dataPoints : [0];
  const minValue = Math.min(...safe, 0);
  const maxValue = Math.max(...safe, 0);

  let yMin = minValue - 5 < 0 ? 0 : minValue - 5;
  let yMax = maxValue + 5;

  if (maxValue === 0) {
    yMin = -3;
    yMax = 3;
  }

  const chartData = {
    labels,
    datasets: [
      {
        label: `Visitors`,
        data: dataPoints,
        borderColor: "rgb(75, 192, 192)",
        backgroundColor: "rgba(75, 192, 192, 0.5)",
        tension: 0.3,
        fill: false,
        pointRadius: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: { min: yMin, max: yMax },
    },
    plugins: {
      legend: { display: false },
    },
  };

  return (
    <div style={{ height: "300px", width: "100%", marginBottom: 20 }}>
      <Line data={chartData} options={options} />
    </div>
  );
};

export default EngagementViewChart;









